import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Table,
  Select,
  Space,
  Typography,
  App,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { qualityService } from '@/services/quality.service';

const { Text } = Typography;

export type InspectionSampleRow = {
  key: string;
  id?: string;
  receiving_inspection_detail_id?: string | null;
  sample_number: number;
  sample_qty: number;
  status: string;
  notes?: string | null;
};

type LineOption = { id: string; label: string };

type Props = {
  inspectionId: string;
  canEdit: boolean;
  lotQuantity?: number | null;
  sampleSize?: number | null;
  samplingPlan?: string | null;
  samples?: Array<{
    id?: string;
    receiving_inspection_detail_id?: string | null;
    sample_number?: number;
    sample_qty?: number;
    status?: string;
    notes?: string | null;
  }>;
  lineOptions?: LineOption[];
  onSaved?: () => void | Promise<void>;
};

const STATUS_OPTIONS = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Pass', label: 'Pass' },
  { value: 'Fail', label: 'Fail' },
  { value: 'Hold', label: 'Hold' },
];

const InspectionSamplesCard: React.FC<Props> = ({
  inspectionId,
  canEdit,
  lotQuantity,
  sampleSize,
  samplingPlan,
  samples,
  lineOptions = [],
  onSaved,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [rows, setRows] = useState<InspectionSampleRow[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      lot_quantity: lotQuantity ?? undefined,
      sample_size: sampleSize ?? undefined,
      sampling_plan: samplingPlan ?? undefined,
    });
    setRows(
      (samples || []).map((s, i) => ({
        key: s.id || `new-${i}-${s.sample_number || i + 1}`,
        id: s.id,
        receiving_inspection_detail_id: s.receiving_inspection_detail_id || null,
        sample_number: s.sample_number ?? i + 1,
        sample_qty: Number(s.sample_qty ?? 1),
        status: s.status || 'Pending',
        notes: s.notes ?? '',
      }))
    );
  }, [lotQuantity, sampleSize, samplingPlan, samples, form]);

  const addRow = () => {
    const nextNum = rows.length ? Math.max(...rows.map((r) => r.sample_number)) + 1 : 1;
    setRows((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        sample_number: nextNum,
        sample_qty: 1,
        status: 'Pending',
        notes: '',
        receiving_inspection_detail_id: null,
      },
    ]);
  };

  const updateRow = (key: string, patch: Partial<InspectionSampleRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const removeRow = (key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  };

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await qualityService.replaceInspectionSampling(inspectionId, {
        lot_quantity: values.lot_quantity ?? null,
        sample_size: values.sample_size ?? null,
        sampling_plan: values.sampling_plan ?? null,
        samples: rows.map((r, i) => ({
          receiving_inspection_detail_id: r.receiving_inspection_detail_id || null,
          sample_number: r.sample_number || i + 1,
          sample_qty: r.sample_qty ?? 1,
          status: r.status || 'Pending',
          notes: r.notes || null,
        })),
      });
      message.success('Sampling saved');
      await onSaved?.();
    } catch (e: unknown) {
      message.error((e as Error)?.message || 'Failed to save sampling');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="Sampling"
      size="small"
      style={{ marginBottom: 16 }}
      extra={
        canEdit ? (
          <Space>
            <Button icon={<PlusOutlined />} onClick={addRow}>
              Add sample
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => void save()}>
              Save sampling
            </Button>
          </Space>
        ) : null
      }
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Record lot size and samples taken. Pass requires all samples to be Passed when sample size or
        samples are recorded.
      </Text>
      <Form form={form} layout="vertical">
        <Space wrap size="middle" style={{ width: '100%', marginBottom: 8 }}>
          <Form.Item name="lot_quantity" label="Lot quantity" style={{ marginBottom: 8, minWidth: 140 }}>
            <InputNumber min={0} step={0.01} disabled={!canEdit} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sample_size" label="Samples taken" style={{ marginBottom: 8, minWidth: 140 }}>
            <InputNumber min={0} step={1} disabled={!canEdit} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sampling_plan" label="Sampling plan / AQL" style={{ marginBottom: 8, minWidth: 220 }}>
            <Input disabled={!canEdit} placeholder="e.g. AQL 1.0 / plan ref" />
          </Form.Item>
        </Space>
      </Form>
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        dataSource={rows}
        locale={{ emptyText: 'No samples recorded yet' }}
        columns={[
          {
            title: '#',
            dataIndex: 'sample_number',
            width: 70,
            render: (v: number, row) =>
              canEdit ? (
                <InputNumber
                  min={1}
                  value={v}
                  onChange={(n) => updateRow(row.key, { sample_number: Number(n) || 1 })}
                />
              ) : (
                v
              ),
          },
          ...(lineOptions.length
            ? [
                {
                  title: 'GRN line',
                  key: 'line',
                  width: 180,
                  render: (_: unknown, row: InspectionSampleRow) =>
                    canEdit ? (
                      <Select
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Optional"
                        value={row.receiving_inspection_detail_id || undefined}
                        options={lineOptions.map((o) => ({ value: o.id, label: o.label }))}
                        onChange={(v) =>
                          updateRow(row.key, { receiving_inspection_detail_id: v || null })
                        }
                      />
                    ) : (
                      lineOptions.find((o) => o.id === row.receiving_inspection_detail_id)?.label ||
                      '—'
                    ),
                },
              ]
            : []),
          {
            title: 'Qty',
            dataIndex: 'sample_qty',
            width: 90,
            render: (v: number, row) =>
              canEdit ? (
                <InputNumber
                  min={0}
                  step={0.01}
                  value={v}
                  onChange={(n) => updateRow(row.key, { sample_qty: Number(n) || 0 })}
                />
              ) : (
                v
              ),
          },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 120,
            render: (v: string, row) =>
              canEdit ? (
                <Select
                  style={{ width: '100%' }}
                  value={v}
                  options={STATUS_OPTIONS}
                  onChange={(status) => updateRow(row.key, { status })}
                />
              ) : (
                v
              ),
          },
          {
            title: 'Notes',
            dataIndex: 'notes',
            render: (v: string, row) =>
              canEdit ? (
                <Input
                  value={v || ''}
                  onChange={(e) => updateRow(row.key, { notes: e.target.value })}
                />
              ) : (
                v || '—'
              ),
          },
          ...(canEdit
            ? [
                {
                  title: '',
                  key: 'actions',
                  width: 48,
                  render: (_: unknown, row: InspectionSampleRow) => (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeRow(row.key)}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />
    </Card>
  );
};

export default InspectionSamplesCard;
